// Combined action bar for assistant messages — replaces MessageExportActions.tsx,
// MessageFeedbackForm.tsx, MessageDebugPanel.tsx, FeedbackButtons.tsx,
// InspectButton.tsx (5 separate Spectrum components) with one registered
// per-message action bar reading debug/export data from
// `message.metadata.custom` (wired in toThreadMessages.ts) instead of 8 props
// threaded through MessageBubble. Native ActionBarPrimitive.Copy handles copy;
// export/feedback/debug are agenthub-specific additions.

import { useState } from 'react';
import { ActionBarPrimitive, AuiIf, useAuiState } from '@assistant-ui/react';
import { CheckIcon, ClipboardIcon, Download, ThumbsDown, ThumbsUp } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { cn } from '@/shared/lib/cn';
import { MessageTiming } from '@/shared/ui/message-timing';
import { useSubmitFeedback } from '../model/useSubmitFeedback';
import { downloadAsMd, downloadAsPdf, buildFilename } from '../model/markdownExport';
import type { FeedbackValue } from '../model/types';

const actionButtonClassName =
  'flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground';

function messageText(content: unknown): string {
  if (!Array.isArray(content)) return '';
  return content
    .filter((p): p is { type: 'text'; text: string } => typeof p === 'object' && p !== null && p.type === 'text')
    .map((p) => p.text)
    .join('');
}

export function ActionBar() {
  const message = useAuiState((s) => s.message);
  const [feedbackState, setFeedbackState] = useState<'idle' | 'up' | 'down' | 'submitting' | 'done'>('idle');
  const [comment, setComment] = useState('');
  const { submitFeedback, isSubmitting, error } = useSubmitFeedback();

  const custom = (message.metadata?.custom ?? {}) as Record<string, unknown>;
  const runId = typeof custom.runId === 'string' ? custom.runId : null;
  const content = messageText(message.content);

  function handleExport(kind: 'md' | 'pdf') {
    const filename = buildFilename(kind, typeof custom.sourceLabel === 'string' ? custom.sourceLabel : undefined);
    if (kind === 'md') downloadAsMd(content, filename);
    else void downloadAsPdf(content, filename);
  }

  function handleThumb(value: FeedbackValue) {
    if (feedbackState !== 'idle') return;
    setFeedbackState(value);
  }

  async function handleSubmitFeedback() {
    if (!runId || feedbackState === 'idle' || feedbackState === 'submitting' || feedbackState === 'done') return;
    if (feedbackState === 'down' && !comment.trim()) return;
    const selectedFeedback = feedbackState;
    setFeedbackState('submitting');
    try {
      await submitFeedback(runId, selectedFeedback, comment.trim() || undefined);
      setFeedbackState('done');
    } catch {
      setFeedbackState(selectedFeedback);
    }
  }

  const hasStreamActivity =
    message.status?.type === 'running' ||
    message.content.some((part) => part.type === 'reasoning' || part.type === 'tool-call') ||
    message.metadata?.timing?.streamStartTime !== undefined ||
    message.metadata?.timing?.totalStreamTime !== undefined;

  return (
    <div className="mt-2 flex flex-col gap-2">
      <div
        className={cn(
          'flex items-center gap-2 transition-opacity',
          hasStreamActivity ? 'opacity-100' : 'opacity-0 group-focus-within/message:opacity-100 group-hover/message:opacity-100',
        )}
      >
        <MessageTiming />
        <ActionBarPrimitive.Root className="flex items-center gap-0.5">
        <ActionBarPrimitive.Copy className={actionButtonClassName}>
          <AuiIf condition={(s) => s.message.isCopied}>
            <CheckIcon className="size-4" />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <ClipboardIcon className="size-4" />
          </AuiIf>
        </ActionBarPrimitive.Copy>

        <DropdownMenu>
          <DropdownMenuTrigger className={actionButtonClassName} aria-label="Export">
            <Download className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => handleExport('md')}>Markdown</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('pdf')}>PDF</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {runId && feedbackState === 'idle' && (
          <>
            <button type="button" aria-label="Helpful" className={actionButtonClassName} onClick={() => handleThumb('up')}>
              <ThumbsUp className="size-4" />
            </button>
            <button type="button" aria-label="Not helpful" className={actionButtonClassName} onClick={() => handleThumb('down')}>
              <ThumbsDown className="size-4" />
            </button>
          </>
        )}
        {runId && feedbackState === 'done' && <span className="text-xs text-muted-foreground">Feedback received</span>}
        </ActionBarPrimitive.Root>
      </div>

      {(feedbackState === 'up' || feedbackState === 'down') && (
        <div className="flex flex-col gap-2 rounded-md border border-border p-2">
          <textarea
            placeholder={feedbackState === 'up' ? 'Optional comment' : 'Why was this not helpful? (required)'}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="w-full resize-none rounded-md border border-border bg-transparent p-2 text-sm outline-none"
          />
          <div className="flex justify-end gap-2">
            <button type="button" className="text-sm text-muted-foreground" onClick={() => { setFeedbackState('idle'); setComment(''); }}>
              Cancel
            </button>
            <button
              type="button"
              disabled={isSubmitting || (feedbackState === 'down' && !comment.trim())}
              className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
              onClick={() => void handleSubmitFeedback()}
            >
              Submit
            </button>
            {error && (
              <span className="text-xs text-destructive" role="alert">
                Feedback failed. Please retry.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
